/* eslint-disable */
// Staff Profile Modal — full 5-tab profile for any staff member
//   Usage: <StaffProfile staff={staffObj} onClose={()=>{}} />
// Tabs: Overview · Salary · Leaves · Timetable · Conduct
// Requires tables: staff_leave_requests, staff_leave_balance, staff_conduct (see SQL in chat)
import React from "react";
import { supabase } from "./supabaseClient";

const C = {
  primary: "#1a6b3c", primaryPale: "#e8f5ee", primaryMid: "#c3e6d0",
  rose: "#e11d48", rosePale: "#fff0f3",
  amber: "#d97706", amberPale: "#fffbeb",
  blue: "#2563eb", bluePale: "#eff6ff",
  purple: "#7c3aed", purplePale: "#f5f3ff",
  teal: "#0d9488", tealPale: "#e0f7f5",
  text: "#111827", muted: "#6b7280", border: "#d1fae5",
  white: "#ffffff", bg: "#f9fafb",
};

const YEAR = String(new Date().getFullYear());

const CONDUCT_TYPES = [
  { id: "commendation", label: "Commendation", color: "#16a34a", bg: "#f0fdf4",  bd: "#bbf7d0", icon: "⭐" },
  { id: "warning",      label: "Warning",      color: "#d97706", bg: "#fffbeb",  bd: "#fde68a", icon: "⚠️" },
  { id: "disciplinary", label: "Disciplinary", color: "#dc2626", bg: "#fef2f2",  bd: "#fecaca", icon: "🚫" },
  { id: "note",         label: "Note",         color: "#374151", bg: "#f9fafb",  bd: "#e5e7eb", icon: "📝" },
];

const ROLES    = ["Teacher", "Admin", "Accountant", "Librarian", "Peon", "Guard", "Cook", "Driver"];
const SUBJECTS = ["Quran Nazra", "Quran Hifz", "Urdu", "English", "Mathematics", "Science", "Social Studies", "Islamiat", "Computer", "General Knowledge", "N/A"];

const fmt    = d => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtCur = n => `PKR ${Number(n || 0).toLocaleString()}`;
const daysBetween = (a, b) => (a && b) ? Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000) + 1) : 0;

const TABS_NAV = [
  { id: "overview",  label: "Overview",  icon: "👤" },
  { id: "salary",    label: "Salary",    icon: "💰" },
  { id: "leaves",    label: "Leaves",    icon: "📅" },
  { id: "timetable", label: "Timetable", icon: "🗓️" },
  { id: "conduct",   label: "Conduct",   icon: "📋" },
];

export function StaffProfile({ staff: initialStaff, onClose }) {
  const [staff,       setStaff]       = React.useState({ ...initialStaff });
  const [tab,         setTab]         = React.useState("overview");
  const [salary,      setSalary]      = React.useState([]);
  const [leaves,      setLeaves]      = React.useState([]);
  const [balance,     setBalance]     = React.useState(null);
  const [timetable,   setTimetable]   = React.useState([]);
  const [conduct,     setConduct]     = React.useState([]);
  const [loadedTabs,  setLoadedTabs]  = React.useState(new Set());
  const [saving,      setSaving]      = React.useState(false);
  const [editInfo,    setEditInfo]    = React.useState(null);
  const [conductForm, setConductForm] = React.useState({ type: "note", title: "", body: "" });
  const [conductMsg,  setConductMsg]  = React.useState(null);
  const [adminEmail,  setAdminEmail]  = React.useState("Admin");
  const [toast,       setToast]       = React.useState(null);

  const showToast = (t, ok = true) => { setToast({ t, ok }); setTimeout(() => setToast(null), 3000); };

  React.useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setAdminEmail(user?.email || "Admin"));
  }, []);

  React.useEffect(() => {
    if (loadedTabs.has(tab)) return;
    setLoadedTabs(p => new Set([...p, tab]));

    switch (tab) {
      case "salary":
        supabase.from("salary_payments").select("*").eq("staff_id", staff.id).order("created_at", { ascending: false })
          .then(({ data }) => setSalary(data || []));
        break;

      case "leaves":
        Promise.all([
          supabase.from("staff_leave_requests").select("*").eq("staff_id", staff.id).order("created_at", { ascending: false }),
          supabase.from("staff_leave_balance").select("*").eq("staff_id", staff.id).eq("year", YEAR).maybeSingle(),
        ]).then(([lr, lb]) => { setLeaves(lr.data || []); setBalance(lb.data || null); });
        break;

      case "timetable":
        supabase.from("timetable").select("*").ilike("teacher_name", `%${staff.name}%`).order("period_no")
          .then(({ data }) => setTimetable(data || []));
        break;

      case "conduct":
        supabase.from("staff_conduct").select("*").eq("staff_id", staff.id).order("date", { ascending: false })
          .then(({ data }) => setConduct(data || []));
        break;
    }
  }, [tab]);

  const saveInfo = async () => {
    if (!editInfo) return;
    setSaving(true);
    const { error } = await supabase.from("staff").update({
      name:         editInfo.name,
      role:         editInfo.role,
      subject:      editInfo.subject      || "",
      phone:        editInfo.phone        || "",
      cnic:         editInfo.cnic         || "",
      email:        editInfo.email        || "",
      salary:       Number(editInfo.salary) || 0,
      joining_date: editInfo.joining_date || null,
      gender:       editInfo.gender       || "",
      address:      editInfo.address      || "",
    }).eq("id", staff.id);
    setSaving(false);
    if (error) { showToast("Error: " + error.message, false); return; }
    setStaff(p => ({ ...p, ...editInfo }));
    setEditInfo(null);
    showToast("Details updated!");
  };

  const saveConduct = async () => {
    if (!conductForm.title.trim()) return setConductMsg({ ok: false, t: "Title is required." });
    setSaving(true);
    const { data, error } = await supabase.from("staff_conduct").insert({
      staff_id:   staff.id,
      staff_name: staff.name,
      date:       new Date().toISOString().split("T")[0],
      type:       conductForm.type,
      title:      conductForm.title.trim(),
      body:       conductForm.body || "",
      added_by:   adminEmail,
    }).select().single();
    setSaving(false);
    if (error) { setConductMsg({ ok: false, t: error.message }); return; }
    setConduct(p => [data, ...p]);
    setConductForm({ type: "note", title: "", body: "" });
    setConductMsg({ ok: true, t: "Entry added." });
  };

  const inp = { width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, boxSizing: "border-box", background: C.white };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.white, borderRadius: 20, width: "100%", maxWidth: 740, maxHeight: "calc(100vh - 32px)", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.3)", overflow: "hidden", position: "relative" }}>

        {toast && (
          <div style={{ position: "absolute", top: 12, right: 12, zIndex: 10, background: toast.ok ? "#f0fdf4" : "#fef2f2", color: toast.ok ? "#16a34a" : "#dc2626", border: `1px solid ${toast.ok ? "#bbf7d0" : "#fecaca"}`, borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 700, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
            {toast.t}
          </div>
        )}

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg,${C.primary},#0a4a24)`, padding: "20px 24px", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 54, height: 54, borderRadius: 14, background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>
                {staff.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div>
                <div style={{ fontWeight: 900, fontSize: 18, color: "white" }}>{staff.name}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>{staff.role} · {staff.subject || "—"}</div>
                <span style={{ background: staff.status === "Active" ? "#16a34a" : "#dc2626", color: "white", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, display: "inline-block", marginTop: 4 }}>
                  {staff.status || "Active"}
                </span>
              </div>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 8, width: 32, height: 32, color: "white", fontWeight: 900, cursor: "pointer", fontSize: 16, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
          </div>

          {/* Tab nav */}
          <div style={{ display: "flex", gap: 6, marginTop: 16, flexWrap: "wrap" }}>
            {TABS_NAV.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ background: tab === t.id ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)", color: "white", border: `1px solid ${tab === t.id ? "rgba(255,255,255,0.5)" : "transparent"}`, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: tab === t.id ? 700 : 500, cursor: "pointer", transition: "all .15s" }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* ── Overview ── */}
          {tab === "overview" && (
            <>
              {/* Quick stats */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {[
                  { icon: "📅", label: "Joined", val: fmt(staff.joining_date), color: C.primary, bg: C.primaryPale },
                  { icon: "💰", label: "Monthly Salary", val: fmtCur(staff.salary), color: C.purple, bg: C.purplePale },
                  { icon: "✅", label: "Status", val: staff.status || "Active", color: staff.status === "Active" ? "#16a34a" : C.rose, bg: staff.status === "Active" ? "#f0fdf4" : C.rosePale },
                ].map((s, i) => (
                  <div key={i} style={{ background: s.bg, borderRadius: 12, padding: "14px 16px", border: `1px solid ${s.color}30` }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: s.color, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: s.color, marginTop: 2 }}>{s.val}</div>
                  </div>
                ))}
              </div>

              {/* Details panel */}
              {!editInfo ? (
                <div style={{ background: C.bg, borderRadius: 14, padding: "16px 20px", border: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: C.text }}>Personal Details</div>
                    <button onClick={() => setEditInfo({ ...staff, salary: String(staff.salary || "") })} style={{ background: C.primaryPale, color: C.primary, border: "none", borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>✏️ Edit</button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[["Full Name", staff.name], ["Role", staff.role], ["Subject", staff.subject || "—"], ["Gender", staff.gender || "—"], ["Phone", staff.phone || "—"], ["CNIC", staff.cnic || "—"], ["Email", staff.email || "—"], ["Joined", fmt(staff.joining_date)], ["Salary", fmtCur(staff.salary) + " / month"], ["Address", staff.address || "—"]].map(([lbl, val]) => (
                      <div key={lbl} style={{ borderBottom: "1px solid #f3f4f6", paddingBottom: 8 }}>
                        <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{lbl}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginTop: 2 }}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ background: C.bg, borderRadius: 14, padding: "16px 20px", border: `1px solid ${C.border}` }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 14 }}>Edit Details</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      ["Full Name", "name", "text"], ["Phone", "phone", "text"],
                      ["CNIC", "cnic", "text"],       ["Email", "email", "email"],
                      ["Salary (PKR)", "salary", "number"], ["Joining Date", "joining_date", "date"],
                      ["Gender", "gender", "text"],    ["Address", "address", "text"],
                    ].map(([lbl, key, type]) => (
                      <div key={key}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#374151", display: "block", marginBottom: 3 }}>{lbl}</label>
                        <input type={type} value={editInfo[key] || ""} onChange={e => setEditInfo(p => ({ ...p, [key]: e.target.value }))} style={inp} />
                      </div>
                    ))}
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: "#374151", display: "block", marginBottom: 3 }}>Role</label>
                      <select value={editInfo.role || ""} onChange={e => setEditInfo(p => ({ ...p, role: e.target.value }))} style={inp}>
                        {ROLES.map(r => <option key={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: "#374151", display: "block", marginBottom: 3 }}>Subject</label>
                      <select value={editInfo.subject || ""} onChange={e => setEditInfo(p => ({ ...p, subject: e.target.value }))} style={inp}>
                        <option value="">— None —</option>
                        {SUBJECTS.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                    <button onClick={saveInfo} disabled={saving} style={{ flex: 1, background: saving ? "#9ca3af" : C.primary, color: C.white, border: "none", borderRadius: 9, padding: 10, fontWeight: 700, fontSize: 13, cursor: saving ? "default" : "pointer" }}>{saving ? "Saving…" : "Save Changes"}</button>
                    <button onClick={() => setEditInfo(null)} style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 9, padding: "10px 18px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Salary ── */}
          {tab === "salary" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {[
                  { label: "Monthly Salary", val: fmtCur(staff.salary), color: C.primary, bg: C.primaryPale },
                  { label: "Total Paid",     val: fmtCur(salary.reduce((a, r) => a + Number(r.amount || 0), 0)), color: C.purple, bg: C.purplePale },
                  { label: "Payments",       val: salary.length, color: C.amber, bg: C.amberPale },
                ].map((s, i) => (
                  <div key={i} style={{ background: s.bg, borderRadius: 12, padding: "14px 16px", border: `1px solid ${s.color}30` }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: s.color, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>{s.val}</div>
                  </div>
                ))}
              </div>
              {salary.length === 0
                ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "24px 0" }}>No salary payments recorded yet.</div>
                : salary.map(r => (
                  <div key={r.id} style={{ background: C.bg, borderRadius: 10, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{r.month} {r.year}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{r.method} · {fmt(r.payment_date)}{r.notes ? ` · ${r.notes}` : ""}</div>
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: C.primary }}>{fmtCur(r.amount)}</div>
                  </div>
                ))
              }
            </>
          )}

          {/* ── Leaves ── */}
          {tab === "leaves" && (
            <>
              {balance ? (
                <div style={{ background: C.bg, borderRadius: 12, padding: "14px 18px", border: `1px solid ${C.border}` }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 12 }}>Leave Balance — {YEAR}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                    {[["Casual", balance.casual_total, balance.casual_used, C.blue], ["Sick", balance.sick_total, balance.sick_used, C.purple], ["Annual", balance.annual_total, balance.annual_used, C.primary]].map(([lbl, total, used, color]) => {
                      const rem = Math.max(0, total - used);
                      const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
                      return (
                        <div key={lbl} style={{ background: C.white, borderRadius: 10, padding: "12px 14px", border: `1px solid ${color}30`, textAlign: "center" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 2 }}>{lbl}</div>
                          <div style={{ fontSize: 22, fontWeight: 900, color }}>{rem}<span style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>/{total}</span></div>
                          <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>used: {used}</div>
                          <div style={{ height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                            <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 2 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={{ background: "#fef3c7", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#92400e", border: "1px solid #fde68a" }}>
                  No leave balance record for {YEAR}. A record will be auto-created when they submit their first request.
                </div>
              )}

              {leaves.length === 0
                ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "24px 0" }}>No leave requests on record.</div>
                : leaves.map(r => {
                  const d  = daysBetween(r.from_date, r.to_date);
                  const tc = { Casual: C.blue, Sick: C.purple, Annual: C.primary, Emergency: C.rose }[r.leave_type] || C.muted;
                  const sc = { pending: { bg: "#fffbeb", fg: "#d97706" }, approved: { bg: "#f0fdf4", fg: "#16a34a" }, rejected: { bg: "#fef2f2", fg: "#dc2626" } }[r.status] || {};
                  return (
                    <div key={r.id} style={{ background: C.bg, borderRadius: 10, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${C.border}`, flexWrap: "wrap", gap: 8 }}>
                      <div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 3 }}>
                          <span style={{ background: `${tc}18`, color: tc, borderRadius: 6, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>{r.leave_type || "Casual"}</span>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{fmt(r.from_date)} → {fmt(r.to_date)}</span>
                          <span style={{ fontSize: 11, color: C.muted }}>({d}d)</span>
                        </div>
                        <div style={{ fontSize: 11, color: C.muted }}>{r.reason || "—"}{r.admin_note ? ` · Admin: ${r.admin_note}` : ""}</div>
                      </div>
                      <span style={{ background: sc.bg, color: sc.fg, borderRadius: 8, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{r.status}</span>
                    </div>
                  );
                })
              }
            </>
          )}

          {/* ── Timetable ── */}
          {tab === "timetable" && (
            <>
              {timetable.length === 0
                ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "24px 0" }}>No timetable entries found for {staff.name}.</div>
                : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(day => {
                  const periods = timetable.filter(t => t.day === day).sort((a, b) => a.period_no - b.period_no);
                  if (!periods.length) return null;
                  return (
                    <div key={day} style={{ background: C.bg, borderRadius: 12, padding: "12px 16px", border: `1px solid ${C.border}` }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: C.primary, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>{day}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {periods.map(p => (
                          <div key={p.id} style={{ background: C.white, borderRadius: 8, padding: "7px 12px", border: `1px solid ${C.border}`, fontSize: 12 }}>
                            <span style={{ fontWeight: 700, color: C.primary }}>P{p.period_no}</span>
                            <span style={{ margin: "0 5px", color: C.muted }}>·</span>
                            <span style={{ fontWeight: 600 }}>{p.subject}</span>
                            <span style={{ margin: "0 5px", color: C.muted }}>·</span>
                            <span style={{ color: C.muted }}>{p.class}</span>
                            {p.time_label && <span style={{ color: C.muted, marginLeft: 4 }}>({p.time_label})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              }
            </>
          )}

          {/* ── Conduct ── */}
          {tab === "conduct" && (
            <>
              {/* Add entry */}
              <div style={{ background: C.bg, borderRadius: 14, padding: "16px 20px", border: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>Add Conduct Entry</div>
                <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                  {CONDUCT_TYPES.map(ct => (
                    <button key={ct.id} onClick={() => setConductForm(p => ({ ...p, type: ct.id }))}
                      style={{ background: conductForm.type === ct.id ? ct.color : ct.bg, color: conductForm.type === ct.id ? C.white : ct.color, border: `1px solid ${ct.bd}`, borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all .15s" }}>
                      {ct.icon} {ct.label}
                    </button>
                  ))}
                </div>
                <input value={conductForm.title} onChange={e => setConductForm(p => ({ ...p, title: e.target.value }))} placeholder="Title / summary…" style={{ ...inp, marginBottom: 8 }} />
                <textarea rows={2} value={conductForm.body} onChange={e => setConductForm(p => ({ ...p, body: e.target.value }))} placeholder="Additional details (optional)…" style={{ ...inp, resize: "vertical", marginBottom: 8 }} />
                {conductMsg && <div style={{ fontSize: 12, color: conductMsg.ok ? "#16a34a" : "#dc2626", marginBottom: 8 }}>{conductMsg.t}</div>}
                <button onClick={saveConduct} disabled={saving}
                  style={{ background: saving ? "#9ca3af" : C.primary, color: C.white, border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 700, fontSize: 13, cursor: saving ? "default" : "pointer" }}>
                  {saving ? "Saving…" : "Add Entry"}
                </button>
              </div>

              {/* Conduct log */}
              {conduct.length === 0
                ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "24px 0" }}>No conduct entries yet.</div>
                : conduct.map(c => {
                  const ct = CONDUCT_TYPES.find(t => t.id === c.type) || CONDUCT_TYPES[3];
                  return (
                    <div key={c.id} style={{ background: ct.bg, borderRadius: 12, padding: "14px 16px", border: `1px solid ${ct.bd}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flex: 1 }}>
                          <span style={{ fontSize: 20, flexShrink: 0 }}>{ct.icon}</span>
                          <div>
                            <div style={{ fontWeight: 800, fontSize: 13, color: ct.color }}>{c.title}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{fmt(c.date)} · Added by: {c.added_by}</div>
                          </div>
                        </div>
                        <span style={{ background: ct.color + "18", color: ct.color, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{ct.label}</span>
                      </div>
                      {c.body && <div style={{ fontSize: 12, color: "#374151", marginTop: 8, lineHeight: 1.6, paddingLeft: 30 }}>{c.body}</div>}
                    </div>
                  );
                })
              }
            </>
          )}

        </div>
      </div>
    </div>
  );
}
